import { useMemo } from "react";
import { useData } from "../context/DataContext";
import { currentInvoicePeriod } from "../lib/invoicing";
import InfoTip from "./InfoTip";
import { CheckCircle2, Clock, Receipt } from "lucide-react";

// Submissions widget: per principal, when was the latest project edit?
// "Updated this month" vs "Not updated this month".

function startOfThisMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });
};

export default function SubmissionsWidget() {
  const { rawData } = useData();
  // useMemo-wrapped so their identity is stable across renders — otherwise the
  // production build (CI=true, warnings as errors) fails on exhaustive-deps.
  const principals = useMemo(() => rawData?.principals || [], [rawData]);
  const projects = useMemo(() => rawData?.projects || [], [rawData]);
  const invoicing = useMemo(() => rawData?.invoicing || {}, [rawData]);
  const period = currentInvoicePeriod();

  const rows = useMemo(() => {
    const start = startOfThisMonth();
    return principals
      .map((pr) => {
        const own = projects.filter((p) => p.principalId === pr.id);
        let latest = null;
        let monthCount = 0;
        for (const p of own) {
          const ts = p.lastEditedAt || null;
          if (!ts) continue;
          if (!latest || ts > latest) latest = ts;
          if (ts >= start && p.lastEditedBy && p.lastEditedBy !== "system_seed") {
            monthCount += 1;
          }
        }
        const invoiceStamp = invoicing?.[pr.id]?.[period.key] || null;
        return {
          ...pr, projects: own.length, latest, monthCount,
          updated: monthCount > 0, invoiceStamp,
        };
      })
      .sort((a, b) => Number(a.updated) - Number(b.updated)); // not-updated first
  }, [principals, projects, invoicing, period.key]);

  if (!rows.length) return null;

  const updatedCount = rows.filter((r) => r.updated).length;

  return (
    <section className="border border-[var(--rule)]" data-testid="submissions-widget">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
        <div className="eyebrow inline-flex items-center">
          Submissions this month
          <InfoTip side="bottom" label="Submissions">
            Per principal, whether any of their projects has been edited so far this month (using <code className="bg-[var(--paper-soft)] px-1">lastEditedAt</code>). Helps chase late submissions.
          </InfoTip>
        </div>
        <div className="eyebrow font-mono-num">
          <span className="text-[var(--ink)]">{updatedCount}</span> / {rows.length} updated
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-5 py-3 border-b md:border-r border-[var(--rule)] last:border-b-0 even:md:border-r-0" data-testid={`submission-${r.id}`}>
            <div className="flex items-center gap-3">
              {r.updated ? (
                <CheckCircle2 size={14} className="text-[#2C4A38]" />
              ) : (
                <Clock size={14} className="text-[#6B2F2A]" />
              )}
              <div>
                <div className="text-sm text-[var(--ink)]">{r.name}</div>
                <div className="eyebrow normal-case tracking-[0.06em] text-[var(--soft-fg)]">
                  {r.projects} project{r.projects !== 1 ? "s" : ""} · last touched {fmtRelative(r.latest)}
                </div>
              </div>
            </div>
            <span className="flex items-center gap-2">
              {r.invoiceStamp && (
                <span
                  className="chip chip-yes inline-flex items-center gap-1"
                  data-testid={`invoicing-confirmed-${r.id}`}
                  title={`Invoicing confirmed by ${r.invoiceStamp.confirmedBy}`}
                >
                  <Receipt size={10} /> Invoicing confirmed
                </span>
              )}
              <span className={`chip ${r.updated ? "chip-yes" : "chip-no"}`}>
                {r.updated ? `${r.monthCount} edited` : "Not updated"}
              </span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
